
import { logger } from "@/utils/logger";
import { supabase } from "@/integrations/supabase/client";

export interface PropertyLocation {
  id: string;
  lat: number;
  lng: number;
  address: string;
  city: string;
  state: string;
}

interface GeocodeResult {
  lat: number;
  lng: number;
}

// In-memory cache for geocoded addresses (fastest, current session only)
const geocodeCache = new Map<string, GeocodeResult>();

// localStorage cache configuration
const LOCALSTORAGE_CACHE_KEY = 'godyspo_geocode_cache';
const MAX_LOCALSTORAGE_ENTRIES = 1000;

interface LocalStorageCacheEntry {
  lat: number;
  lng: number;
  timestamp: number;
}

interface LocalStorageCache {
  [address: string]: LocalStorageCacheEntry;
}

// Get localStorage cache
const getLocalStorageCache = (): LocalStorageCache => {
  try {
    const cached = localStorage.getItem(LOCALSTORAGE_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

// Save to localStorage cache with LRU eviction
const saveToLocalStorageCache = (address: string, result: GeocodeResult): void => {
  try {
    const cache = getLocalStorageCache();
    
    // Add new entry
    cache[address] = {
      lat: result.lat,
      lng: result.lng,
      timestamp: Date.now()
    };
    
    // If cache exceeds max size, remove oldest entries
    const entries = Object.entries(cache);
    if (entries.length > MAX_LOCALSTORAGE_ENTRIES) {
      // Sort by timestamp (oldest first) and remove excess
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, entries.length - MAX_LOCALSTORAGE_ENTRIES);
      toRemove.forEach(([key]) => delete cache[key]);
    }
    
    localStorage.setItem(LOCALSTORAGE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // localStorage might be full or unavailable
    logger.warn("Failed to save geocode to localStorage cache", { error });
  }
};

// Get from localStorage cache
const getFromLocalStorageCache = (address: string): GeocodeResult | null => {
  try {
    const cache = getLocalStorageCache();
    const entry = cache[address];
    if (entry) {
      return { lat: entry.lat, lng: entry.lng };
    }
    return null;
  } catch {
    return null;
  }
};

// Build full address key for caching
const buildAddressKey = (address: string, city: string, state: string, zipCode?: string): string => {
  return `${address}, ${city}, ${state} ${zipCode || ''}`.trim().toLowerCase();
};

export const geocodeAddress = async (
  address: string,
  city: string,
  state: string,
  zipCode?: string
): Promise<GeocodeResult | null> => {
  const addressKey = buildAddressKey(address, city, state, zipCode);
  
  // Layer 1: Check in-memory cache first (fastest)
  if (geocodeCache.has(addressKey)) {
    return geocodeCache.get(addressKey)!;
  }
  
  // Layer 2: Check localStorage cache
  const localStorageResult = getFromLocalStorageCache(addressKey);
  if (localStorageResult) {
    // Populate in-memory cache for faster subsequent access
    geocodeCache.set(addressKey, localStorageResult);
    return localStorageResult;
  }

  // Layer 3: Call Google Maps API
  try {
    const { data, error } = await supabase.functions.invoke('google-maps-geocode', {
      body: {
        address,
        city,
        state,
        zipCode
      }
    });

    if (error) {
      logger.error("Error calling geocoding function", { error });
      return null;
    }

    if (data && data.lat && data.lng) {
      const result = { lat: data.lat, lng: data.lng };
      
      // Cache the result in both layers
      geocodeCache.set(addressKey, result);
      saveToLocalStorageCache(addressKey, result);
      
      return result;
    }
    
    logger.warn("Geocoding failed for address", { addressKey });
    return null;
  } catch (error) {
    logger.error("Error geocoding address", { error });
    return null;
  }
};

// Property interface with optional coordinates
interface PropertyWithOptionalCoords {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number | null;
  longitude?: number | null;
}

export const geocodeProperties = async (properties: PropertyWithOptionalCoords[]): Promise<PropertyLocation[]> => {
  const locations: PropertyLocation[] = [];
  const propertiesToGeocode: PropertyWithOptionalCoords[] = [];
  
  // First pass: Use stored coordinates where available
  for (const property of properties) {
    // Layer 0: Check if property has stored coordinates in database
    if (property.latitude != null && property.longitude != null) {
      locations.push({
        id: property.id,
        lat: property.latitude,
        lng: property.longitude,
        address: property.address,
        city: property.city,
        state: property.state,
      });
      continue;
    }
    
    // Check caches before adding to geocode queue
    const addressKey = buildAddressKey(property.address, property.city, property.state, property.zip_code);
    
    // Layer 1: Check in-memory cache
    if (geocodeCache.has(addressKey)) {
      const cached = geocodeCache.get(addressKey)!;
      locations.push({
        id: property.id,
        lat: cached.lat,
        lng: cached.lng,
        address: property.address,
        city: property.city,
        state: property.state,
      });
      continue;
    }
    
    // Layer 2: Check localStorage cache
    const localStorageResult = getFromLocalStorageCache(addressKey);
    if (localStorageResult) {
      // Populate in-memory cache
      geocodeCache.set(addressKey, localStorageResult);
      locations.push({
        id: property.id,
        lat: localStorageResult.lat,
        lng: localStorageResult.lng,
        address: property.address,
        city: property.city,
        state: property.state,
      });
      continue;
    }
    
    // Needs API geocoding
    propertiesToGeocode.push(property);
  }
  
  // Process properties that need API geocoding in batches
  const batchSize = 10;
  for (let i = 0; i < propertiesToGeocode.length; i += batchSize) {
    const batch = propertiesToGeocode.slice(i, i + batchSize);
    
    const batchPromises = batch.map(async (property) => {
      const location = await geocodeAddress(
        property.address,
        property.city,
        property.state,
        property.zip_code
      );
      
      if (location) {
        return {
          id: property.id,
          lat: location.lat,
          lng: location.lng,
          address: property.address,
          city: property.city,
          state: property.state,
        };
      }
      return null;
    });
    
    const batchResults = await Promise.all(batchPromises);
    locations.push(...batchResults.filter(Boolean) as PropertyLocation[]);
    
    // Small delay between batches to respect rate limits
    if (i + batchSize < propertiesToGeocode.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return locations;
};

// Save coordinates to database for a property
export const savePropertyCoordinates = async (
  propertyId: string,
  latitude: number,
  longitude: number
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('properties')
      .update({ latitude, longitude })
      .eq('id', propertyId);
    
    if (error) {
      logger.error("Failed to save property coordinates", { error, propertyId });
      return false;
    }
    
    return true;
  } catch (error) {
    logger.error("Error saving property coordinates", { error, propertyId });
    return false;
  }
};
